import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import {
  Fuel,
  Percent,
  Route,
  Sparkles,
  PiggyBank,
  Map as MapIcon,
  Search,
  Scale,
  BadgeCheck,
  Car,
  Briefcase,
  Truck,
  Users,
  CircleUser,
  ArrowRight,
  Apple,
  Play,
} from 'lucide-react';
import { SEO } from '../components/SEO';
import { PhoneSlideshow, PhoneStill } from '../components/fueliq/PhoneShowcase';

const bigPicture = [
  {
    icon: Fuel,
    title: 'Fuel prices',
    desc: 'Compare prices at nearby stations.',
  },
  {
    icon: Percent,
    title: 'Your discounts',
    desc: 'Factor in eligible discounts to compare effective prices.',
  },
  {
    icon: Route,
    title: 'Distance matters',
    desc: 'Consider whether the extra drive is worthwhile.',
  },
  {
    icon: Sparkles,
    title: 'Smarter decisions',
    desc: 'Identify stations offering better overall value.',
  },
];

const steps = [
  {
    num: '01',
    icon: Search,
    title: 'Find nearby stations',
    desc: 'Explore fuel stations around you and compare available prices.',
  },
  {
    num: '02',
    icon: Scale,
    title: 'Compare your options',
    desc: 'Factor in your fuel discount and the distance to each station.',
  },
  {
    num: '03',
    icon: BadgeCheck,
    title: 'Choose better value',
    desc: 'See which station could offer the best overall value for your refuelling trip.',
  },
];

const features = [
  {
    icon: Fuel,
    title: 'Fuel-price comparisons',
    desc: 'Explore available fuel prices and compare nearby stations.',
  },
  {
    icon: Percent,
    title: 'Discount-aware comparisons',
    desc: 'See how applicable discounts change the effective fuel price.',
  },
  {
    icon: Route,
    title: 'Distance-conscious recommendations',
    desc: 'Evaluate whether travelling farther for a lower price makes sense.',
  },
  {
    icon: PiggyBank,
    title: 'Potential savings',
    desc: 'Understand the potential savings from choosing a different station.',
  },
  {
    icon: MapIcon,
    title: 'Map-based discovery',
    desc: 'Explore stations on a map and find options near your location.',
  },
];

const audiences = [
  { icon: Car, label: 'Everyday motorists managing household expenses' },
  { icon: Briefcase, label: 'Daily commuters looking for better-value fuel' },
  { icon: Truck, label: 'Delivery drivers trying to manage operating costs' },
  { icon: Users, label: 'Rideshare and taxi drivers who refuel frequently' },
  { icon: CircleUser, label: 'Anyone who wants more informed fuel-buying decisions' },
];

export default function FuelIQPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="FuelIQ — Smart Fuel-Price Comparison App"
        path="/fueliq"
        description="FuelIQ helps Australian drivers compare nearby fuel prices, factor in fuel discounts, and decide whether driving a little farther is actually worth it. Find. Save. Go Further."
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-24 bg-[#0a1f33] overflow-hidden" data-testid="fueliq-hero">
          <div className="absolute -top-24 -right-16 h-96 w-96 rounded-full bg-[#3BA6E0]/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 h-96 w-96 rounded-full bg-[#8FD14F]/15 blur-3xl" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="h-14 w-14 rounded-2xl overflow-hidden ring-1 ring-white/10">
                    <img src="/images/fueliq-logo.png" alt="FuelIQ logo" className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white leading-none">FuelIQ</p>
                    <p className="text-sm font-medium bg-gradient-to-r from-[#9FE24A] to-[#3BA6E0] bg-clip-text text-transparent">
                      Find. Save. Go Further.
                    </p>
                  </div>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Smarter fuel choices
                  <span className="block bg-gradient-to-r from-[#9FE24A] to-[#3BA6E0] bg-clip-text text-transparent">
                    start here.
                  </span>
                </h1>
                <p className="text-lg text-slate-300 mb-8 max-w-xl">
                  FuelIQ helps you compare nearby fuel prices, factor in your fuel discounts, and
                  discover whether driving a little farther could save you money.
                </p>

                <div className="flex flex-wrap gap-4">
                  <a href="#features">
                    <Button
                      size="lg"
                      data-testid="hero-explore-btn"
                      className="bg-gradient-to-r from-[#8FD14F] to-[#3BA6E0] hover:opacity-90 text-white font-semibold px-8 rounded-xl border-0"
                    >
                      Explore FuelIQ
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                  <a href="#how-it-works">
                    <Button
                      size="lg"
                      variant="outline"
                      data-testid="hero-how-btn"
                      className="border-white/30 text-white hover:bg-white/10 px-8 rounded-xl bg-transparent"
                    >
                      How It Works
                    </Button>
                  </a>
                </div>

                <p className="text-sm text-slate-400 mt-6">Make every refuelling decision count.</p>
              </div>

              <div className="flex justify-center lg:justify-end">
                <PhoneSlideshow testid="hero-slideshow" />
              </div>
            </div>
          </div>
        </section>

        {/* The bigger picture */}
        <section className="py-20 lg:py-28 bg-white" data-testid="bigger-picture-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-5">
                Make every refuelling decision count.
              </h2>
              <p className="text-lg text-slate-600">
                A station might advertise a lower price, but what happens when it's farther away?
                And what if you have a fuel discount at another station? FuelIQ helps you consider
                the bigger picture.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bigPicture.map((item) => (
                <div
                  key={item.title}
                  data-testid={`bigpicture-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group rounded-2xl border border-slate-200 p-6 hover:border-[#3BA6E0]/50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#8FD14F]/15 to-[#3BA6E0]/15 mb-5">
                    <item.icon className="h-6 w-6 text-[#199473]" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-20 lg:py-28 bg-slate-50 scroll-mt-20" data-testid="how-it-works-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-white text-[#199473] text-xs font-semibold tracking-wide uppercase mb-4 border border-slate-200">
                How It Works
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-900">
                Three simple steps to better value
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step) => (
                <div
                  key={step.num}
                  data-testid={`step-${step.num}`}
                  className="relative rounded-2xl bg-white border border-slate-200 p-8 hover:shadow-xl transition-all duration-300"
                >
                  <span className="absolute top-6 right-6 text-5xl font-bold text-slate-100 select-none">
                    {step.num}
                  </span>
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#8FD14F] to-[#3BA6E0] mb-6">
                    <step.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-navy-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-20 lg:py-28 bg-white scroll-mt-20" data-testid="features-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-slate-100 text-[#199473] text-xs font-semibold tracking-wide uppercase mb-4">
                Features
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-900">
                Everything you need to fuel up smarter
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  data-testid={`feature-${feature.title.toLowerCase().split(' ')[0]}`}
                  className="group rounded-2xl border border-slate-200 p-7 hover:-translate-y-1 hover:shadow-xl hover:border-[#8FD14F]/50 transition-all duration-300"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#8FD14F]/15 to-[#3BA6E0]/15 mb-5 group-hover:from-[#8FD14F] group-hover:to-[#3BA6E0] transition-all duration-300">
                    <feature.icon className="h-6 w-6 text-[#199473] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>

            {/* See it in action — real app screens */}
            <div className="mt-20" data-testid="screens-showcase">
              <div className="text-center max-w-2xl mx-auto mb-12">
                <span className="inline-block px-3 py-1 rounded-full bg-slate-100 text-[#199473] text-xs font-semibold tracking-wide uppercase mb-4">
                  See it in action
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-navy-900">
                  A closer look at FuelIQ
                </h3>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
                {[
                  { src: '/images/fueliq/screen-4.webp', label: 'Your fuel snapshot' },
                  { src: '/images/fueliq/screen-5.webp', label: 'Map-based discovery' },
                  { src: '/images/fueliq/screen-8.webp', label: 'Find cheapest fuel' },
                  { src: '/images/fueliq/screen-7.webp', label: 'Filter & compare' },
                ].map((s) => (
                  <div key={s.src} className="flex flex-col items-center gap-4">
                    <PhoneStill src={s.src} alt={`FuelIQ — ${s.label}`} widthClass="w-[170px]" />
                    <span className="text-sm font-medium text-slate-600 text-center">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section className="py-20 lg:py-28 bg-slate-50" data-testid="audience-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-5">
                  Who FuelIQ can help
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  Whether you're watching the household budget or managing fuel as a business cost,
                  FuelIQ helps you make more informed decisions at the pump.
                </p>
                <Link to="/fueliq#download">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-[#8FD14F] to-[#3BA6E0] hover:opacity-90 text-white font-semibold px-8 rounded-xl border-0"
                  >
                    Find. Save. Go Further.
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="space-y-4">
                {audiences.map((a) => (
                  <div
                    key={a.label}
                    className="flex items-center gap-4 rounded-xl bg-white border border-slate-200 p-4"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#8FD14F]/15 to-[#3BA6E0]/15">
                      <a.icon className="h-5 w-5 text-[#199473]" />
                    </div>
                    <span className="text-slate-700 font-medium">{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA / Download */}
        <section id="download" className="py-20 lg:py-28 bg-[#0a1f33] relative overflow-hidden scroll-mt-20" data-testid="download-section">
          <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-[#8FD14F]/15 blur-3xl" />
          <div className="absolute -bottom-24 left-0 h-96 w-96 rounded-full bg-[#3BA6E0]/20 blur-3xl" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Compare prices. Consider your discounts. Choose with confidence.
            </h2>
            <p className="text-xl font-medium bg-gradient-to-r from-[#9FE24A] to-[#3BA6E0] bg-clip-text text-transparent mb-10">
              FuelIQ — Find. Save. Go Further.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                disabled
                data-testid="app-store-btn"
                className="flex items-center gap-3 rounded-xl bg-white/10 ring-1 ring-white/15 px-6 py-3 text-left disabled:opacity-80 cursor-default"
              >
                <Apple className="h-7 w-7 text-white" />
                <span>
                  <span className="block text-[10px] text-slate-300 leading-none">Download on the</span>
                  <span className="block text-base font-semibold text-white">App Store</span>
                </span>
              </button>
              <button
                disabled
                data-testid="play-store-btn"
                className="flex items-center gap-3 rounded-xl bg-white/10 ring-1 ring-white/15 px-6 py-3 text-left disabled:opacity-80 cursor-default"
              >
                <Play className="h-6 w-6 text-white fill-white" />
                <span>
                  <span className="block text-[10px] text-slate-300 leading-none">Get it on</span>
                  <span className="block text-base font-semibold text-white">Google Play</span>
                </span>
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-5">Coming soon to the App Store and Google Play.</p>
          </div>
        </section>
      </main>

      {/* FuelIQ Footer */}
      <footer className="bg-[#081826] text-slate-300" data-testid="fueliq-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="lg:col-span-2 max-w-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 rounded-xl overflow-hidden ring-1 ring-white/10">
                  <img src="/images/fueliq-logo.png" alt="FuelIQ logo" className="h-full w-full object-cover" />
                </div>
                <div>
                  <p className="text-lg font-bold text-white leading-none">FuelIQ</p>
                  <p className="text-xs bg-gradient-to-r from-[#9FE24A] to-[#3BA6E0] bg-clip-text text-transparent font-medium">
                    Find. Save. Go Further.
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">
                FuelIQ helps Australian drivers compare nearby fuel prices, factor in eligible
                discounts, and decide whether driving a little farther is actually worth it.
              </p>
              <p className="text-xs text-slate-500">
                A digital venture by Naviton Global Pty Ltd.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">FuelIQ</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#features" className="hover:text-white transition-colors" data-testid="footer-about-link">
                    About FuelIQ
                  </a>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact-link">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">
                    Terms of Use
                  </Link>
                </li>
              </ul>
            </div>

            {/* Data source */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Data</h4>
              <p className="text-sm text-slate-400 leading-relaxed">
                Fuel prices, station locations, and savings estimates are provided for general
                informational purposes and may not always be accurate or current. Always confirm
                prices at the station before purchasing.
              </p>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} Naviton Global Pty Ltd. All rights reserved. FuelIQ is a
              trade mark of Naviton Global Pty Ltd.
            </p>
            <Link to="/products" className="text-xs text-slate-400 hover:text-white transition-colors">
              ← Back to Naviton Global
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
